/**
 * sticky-image-stack.js
 * Vanilla JS — Shopify OS 2.0 Sticky Image Stack section
 *
 * Uses IntersectionObserver to:
 *  1. Add `is-active` class when a card wrapper enters the viewport.
 *  2. Remove `is-active` when it leaves.
 *  3. Briefly add `is-entering` for the scale-in micro-animation.
 *
 * No jQuery. No layout shift. Hardware-accelerated only.
 */

(function () {
  'use strict';

  /**
   * Boot function — called once per section instance.
   * @param {HTMLElement} section
   */
  function initStickyImageStack(section) {
    const wrappers = section.querySelectorAll('.sis-card-wrapper');
    if (!wrappers.length) return;

    const isFadeEnabled = section.dataset.fade === 'true';
    const isScaleEnabled = section.dataset.scale === 'true';

    /* ── Parent Overflow Corrector for Sticky Positioning ── */
    let parent = section.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      if (style.overflowX === 'hidden' || style.overflowY === 'hidden' || style.overflow === 'hidden') {
        parent.style.overflow = 'visible';
      }
      parent = parent.parentElement;
    }

    /* ── Dynamic Sticky Top Offset (Header Offset) ── */
    function calculateStickyTop() {
      let offset = 0;
      const header = document.querySelector('header, .header, .header-wrapper, #shopify-section-header, [id*="header"], [class*="header"]');
      if (header) {
        const headerStyle = window.getComputedStyle(header);
        const position = headerStyle.position;
        if (position === 'fixed' || position === 'sticky' || header.closest('[style*="position: fixed"]') || header.closest('[style*="position: sticky"]')) {
          offset = header.offsetHeight;
        }
      }
      section.style.setProperty('--sis-sticky-top', offset + 'px');
    }
    calculateStickyTop();
    window.addEventListener('resize', calculateStickyTop);
    window.addEventListener('scroll', calculateStickyTop, { passive: true });

    /* ── IntersectionObserver ─────────────────────────────────── */
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const wrapper = entry.target;

          if (entry.isIntersecting) {
            /* Card entering viewport */
            wrapper.classList.add('is-active');

            if (isScaleEnabled) {
              /* Short flash of "entering" class for scale micro-animation */
              wrapper.classList.add('is-entering');
              requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                  wrapper.classList.remove('is-entering');
                });
              });
            }
          } else {
            /* Card leaving viewport */
            wrapper.classList.remove('is-active', 'is-entering');
          }
        });
      },
      {
        root: null,           /* relative to viewport */
        threshold: 0.01,      /* fire as soon as 1% visible */
        rootMargin: '0px'
      }
    );

    wrappers.forEach(function (wrapper) {
      observer.observe(wrapper);
    });

    /* ── Keyboard accessibility for image links ───────────────── */
    const imgLinks = section.querySelectorAll('.sis-card__img-link');
    imgLinks.forEach(function (link) {
      /* Ensure link is reachable via Tab */
      if (!link.getAttribute('href') || link.getAttribute('href') === '#') {
        link.setAttribute('tabindex', '-1');
        link.setAttribute('aria-hidden', 'true');
      } else {
        link.setAttribute('tabindex', '0');
        /* Enter key fires click */
        link.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            link.click();
          }
        });
      }
    });

    /* ── Cleanup on Shopify section reload (Theme Editor) ──────── */
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target === section) {
        observer.disconnect();
        window.removeEventListener('resize', calculateStickyTop);
        window.removeEventListener('scroll', calculateStickyTop);
      }
    });

    /* ── Re-init on Shopify block select (Theme Editor live) ───── */
    document.addEventListener('shopify:block:select', function (e) {
      const block = e.target;
      if (!section.contains(block)) return;
      const wrapper = block.closest('.sis-card-wrapper');
      if (wrapper) {
        wrapper.classList.add('is-active');
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    document.addEventListener('shopify:block:deselect', function (e) {
      const block = e.target;
      if (!section.contains(block)) return;
      const wrapper = block.closest('.sis-card-wrapper');
      if (wrapper) wrapper.classList.remove('is-active');
    });
  }

  /* ── Entry point ──────────────────────────────────────────────── */
  function boot() {
    document
      .querySelectorAll('.sis-section')
      .forEach(function (section) {
        initStickyImageStack(section);
      });
  }

  /* Run after DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor: re-boot when section is loaded/reloaded */
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.sis-section');
    if (section) initStickyImageStack(section);
  });
})();
